const DECIMAL_NUMBER = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i;

// Normalize finite JS numbers (including numeric form values) to a decimal
// coefficient. Do not let blank fields, null, booleans or partial strings
// silently become zero as Number()/parseFloat() would.
function decimal(value, field, nonnegative = false) {
  if (
    !["number", "string"].includes(typeof value) ||
    (typeof value === "string" && !DECIMAL_NUMBER.test(value.trim()))
  ) {
    throw new TypeError(`${field} must be a finite decimal number`);
  }

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    throw new RangeError(`${field} must be finite`);
  }
  if (
    typeof value === "string" &&
    numeric === 0 &&
    /[1-9]/.test(value.trim().split(/[eE]/)[0])
  ) {
    throw new RangeError(`${field} is below the supported numeric range`);
  }
  if (nonnegative && numeric < 0) {
    throw new RangeError(`${field} must be nonnegative`);
  }

  const [mantissa, exponent = "0"] = numeric.toString().toLowerCase().split("e");
  const [whole, fraction = ""] = mantissa.split(".");
  let coefficient = BigInt(whole + fraction);
  let scale = fraction.length - Number(exponent);
  if (scale < 0) {
    coefficient *= 10n ** BigInt(-scale);
    scale = 0;
  }
  return { coefficient, scale };
}

function roundedFourPlaces({ coefficient, scale }) {
  let rounded;
  if (scale <= 4) {
    rounded = coefficient * 10n ** BigInt(4 - scale);
  } else {
    const divisor = 10n ** BigInt(scale - 4);
    const absolute = coefficient < 0n ? -coefficient : coefficient;
    rounded = absolute / divisor;
    if ((absolute % divisor) * 2n >= divisor) rounded += 1n;
    if (coefficient < 0n) rounded = -rounded;
  }
  const result = Number(rounded) / 10000;
  if (!Number.isFinite(result)) {
    throw new RangeError("Rounded cost exceeds the supported numeric range");
  }
  return result === 0 ? 0 : result;
}

/**
 * Round a finite decimal value to four places, with exact decimal midpoint
 * ties away from zero, matching SQL ROUND's commercial rounding policy.
 * Inputs remain JS numbers: this does not reproduce every binary FLOAT
 * intermediate in a database. Decimal arithmetic avoids an extra JS rounding
 * error at e.g. 1.00005 and rounds only after the complete cost formula.
 */
export function round4(value) {
  return roundedFourPlaces(decimal(value, "value"));
}

/**
 * USD/part = (part grams + returned runner grams) / 1000 * USD/kg.
 * Mirrors QC_SP_Plastics.sql's MaterialCost formula. Runner weight must be
 * explicitly supplied from the calculation response; zero is valid, missing
 * is not. No runner division, regrind credit or new manufacturing policy is
 * applied here. All three inputs permit zero but reject negative values.
 */
export function materialCostPerPart({
  partWeightGrams,
  runnerWeightGrams,
  pricePerKg,
}) {
  const part = decimal(partWeightGrams, "partWeightGrams", true);
  const runner = decimal(runnerWeightGrams, "runnerWeightGrams", true);
  const price = decimal(pricePerKg, "pricePerKg", true);
  const weightScale = Math.max(part.scale, runner.scale);
  const grams =
    part.coefficient * 10n ** BigInt(weightScale - part.scale) +
    runner.coefficient * 10n ** BigInt(weightScale - runner.scale);

  return roundedFourPlaces({
    coefficient: grams * price.coefficient,
    scale: weightScale + price.scale + 3,
  });
}

/** Explicit percent, including 0.5 meaning 0.5%, never a decimal fraction. */
export function overheadCostPerPart({ materialCost, processCost, ratePercent }) {
  const material = decimal(materialCost, "materialCost", true);
  const process = decimal(processCost, "processCost", true);
  const rate = decimal(ratePercent, "ratePercent", true);
  const baseScale = Math.max(material.scale, process.scale);
  const base = material.coefficient * 10n ** BigInt(baseScale - material.scale) +
    process.coefficient * 10n ** BigInt(baseScale - process.scale);
  return roundedFourPlaces({ coefficient: base * rate.coefficient, scale: baseScale + rate.scale + 2 });
}
